if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      this.form.querySelector('[name=id]').disabled = false;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cartNotification = document.querySelector('cart-notification');
      this.submitButton = this.querySelector('[type="submit"]');
      this.artworkInput = this.findArtworkInput_();

      if (this.artworkInput && this.artworkInput.required) {
        this.setupArtworkRequirement_();
      }
    }

    findArtworkInput_() {
      const selector = 'input[type="file"][name="properties[Logo / artwork file]"]';
      return this.form.querySelector(selector) ||
        document.querySelector(selector + '[form="' + this.form.id + '"]');
    }

    setupArtworkRequirement_() {
      this.artworkInitiallyUnavailable = this.submitButton.disabled;
      this.artworkInput.addEventListener('change', () => {
        this.updateArtworkButtonState_();
      });
      this.updateArtworkButtonState_();
    }

    selectedArtworkFile_() {
      const file = this.artworkInput && this.artworkInput.files && this.artworkInput.files[0];
      return file && file.name && file.size > 0 ? file : null;
    }

    updateArtworkButtonState_() {
      if (!this.artworkInput || this.artworkInitiallyUnavailable) return;

      if (this.selectedArtworkFile_()) {
        if (this.submitButton.dataset.lbtArtworkLocked === 'true') {
          this.submitButton.disabled = false;
          this.submitButton.removeAttribute('aria-disabled');
          delete this.submitButton.dataset.lbtArtworkLocked;
        }
      } else {
        this.submitButton.disabled = true;
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.dataset.lbtArtworkLocked = 'true';
      }
    }

    isValidShopifyArtworkUrl_(value) {
      if (typeof value !== 'string' || !value.trim()) return false;
      const normalized = value.trim();
      if (/fakepath/i.test(normalized) || !/^https:\/\//i.test(normalized)) return false;

      try {
        return new URL(normalized).hostname.toLowerCase() === 'cdn.shopify.com';
      } catch (error) {
        return false;
      }
    }

    async removeInvalidArtworkLine_(response) {
      let lineKey = response && response.key;

      if (!lineKey) {
        const cartResponse = await fetch('/cart.js', {
          headers: { Accept: 'application/json' }
        });
        const cart = await cartResponse.json();
        const selectedFile = this.selectedArtworkFile_();
        const originalName = selectedFile && selectedFile.name;
        const invalidLine = (cart.items || []).find((item) => {
          const properties = item.properties || {};
          return item.id === response.id &&
            properties['_Artwork file name'] === originalName &&
            !this.isValidShopifyArtworkUrl_(properties['Logo / artwork file']);
        });
        lineKey = invalidLine && invalidLine.key;
      }

      if (!lineKey) return;

      await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ id: lineKey, quantity: 0 })
      });
    }

    resetArtworkSelection_() {
      if (!this.artworkInput) return;
      this.artworkInput.value = '';
      this.artworkInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    async onSubmitHandler(evt) {
      evt.preventDefault();

      if (this.artworkInput && !this.selectedArtworkFile_()) {
        this.handleErrorMessage('Please select your logo or artwork file before adding this product to the cart.');
        this.updateArtworkButtonState_();
        return;
      }

      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this.handleErrorMessage();
      this.cartNotification.setActiveElement(document.activeElement);

      this.submitButton.setAttribute('aria-disabled', 'true');
      this.submitButton.classList.add('loading');
      this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(this.form);
      formData.append('sections', this.cartNotification.getSectionsToRender().map((section) => section.id));
      formData.append('sections_url', window.location.pathname);
      config.body = formData;

      try {
        const fetchResponse = await fetch(`${routes.cart_add_url}`, config);
        const response = await fetchResponse.json();

        if (response.status) {
          this.handleErrorMessage(response.description);

          const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
          if (soldOutMessage) {
            this.submitButton.setAttribute('aria-disabled', 'true');
            this.submitButton.querySelector('span').classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
          }
          this.error = true;
          return;
        }

        if (this.artworkInput) {
          const artworkUrl = response.properties && response.properties['Logo / artwork file'];

          if (!this.isValidShopifyArtworkUrl_(artworkUrl)) {
            await this.removeInvalidArtworkLine_(response);
            this.resetArtworkSelection_();
            this.handleErrorMessage('We could not securely attach that file. Please select it again and then use Add to cart.');
            this.error = true;
            return;
          }

          const file = this.selectedArtworkFile_();
          this.artworkInput.dispatchEvent(new CustomEvent('lbt:artwork-attached', {
            bubbles: true,
            detail: {
              filename: file ? file.name : '',
              url: artworkUrl
            }
          }));
        }

        this.error = false;
        const quickAddModal = this.closest('quick-add-modal');
        if (quickAddModal) {
          document.body.addEventListener('modalClosed', () => {
            setTimeout(() => { this.cartNotification.renderContents(response); });
          }, { once: true });
          quickAddModal.hide(true);
        } else {
          this.cartNotification.renderContents(response);
        }
      } catch (error) {
        console.error(error);
        this.handleErrorMessage('We could not attach your file. Please try again.');
        this.error = true;
      } finally {
        this.submitButton.classList.remove('loading');
        if (!this.error) this.submitButton.removeAttribute('aria-disabled');
        this.querySelector('.loading-overlay__spinner').classList.add('hidden');
        if (this.artworkInput) this.updateArtworkButtonState_();
      }
    }

    handleErrorMessage(errorMessage = false) {
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}
